import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useWorkspace } from '../context/WorkspaceContext';
import { listStakeholderViews } from '../utils/stakeholderApi';

/**
 * The members-facing index of stakeholder views: a plain list of the saved
 * "what's shipping" lenses across every Shape Up organization the viewer belongs
 * to. No edit controls — configuration lives in organization settings. Each card
 * opens the read-only view page.
 */
const StakeholderViewsListPage = () => {
  const { organizations } = useWorkspace();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchViews = useCallback(async () => {
    setLoading(true);
    // Surface views from any organization that has Shape Up on, so the page is
    // useful no matter which workspace is currently selected.
    const shapeUpOrgs = organizations.filter(
      org => ['shape-up', 'both'].includes(org.settings?.methodology)
    );
    try {
      const perOrg = await Promise.all(
        shapeUpOrgs.map(org =>
          listStakeholderViews(org.id)
            .then(list => (Array.isArray(list) ? list.map(v => ({ ...v, orgName: org.name })) : []))
            .catch(() => [])
        )
      );
      setViews(perOrg.flat());
    } finally {
      setLoading(false);
    }
  }, [organizations]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Stakeholder views</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Plain-language read on what Product and Engineering are shipping, and roughly when.
      </Typography>

      {views.length === 0 ? (
        <Typography color="text.secondary">
          No stakeholder views yet. An organization admin can create one in organization settings.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {views.map((view) => (
            <Card key={view.id} variant="outlined">
              <CardActionArea component={RouterLink} to={`/shape-up/stakeholders/${view.id}`}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{view.name}</Typography>
                    <Chip size="small" variant="outlined" label={view.orgName} />
                  </Box>
                  {view.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {view.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default StakeholderViewsListPage;
